import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tab, Tabs } from "@mui/material";

import SectionHeader from "../../Components/Shared/SectionHeader/SectionHeader";
import { getCurrentEmployee } from "../../Service/EmployeeService/EmployeeService";
import { userUpdate } from "../../Redux/User/UserAction";
import { handleApiError } from "../../Utils/apiErrors";
import UsersTab from "./UsersTab";
import RolesTab from "./RolesTab";
import PermissionsTab from "./PermissionsTab";
import { UsersTabProps } from "./userUtils";
import "../../Components/Shared/EntitySection/EntitySection.css";
import "./Users.css";

// Gestión de usuarios (permiso gestionar_usuarios): pestañas Usuarios, Roles
// y Permisos dentro de la misma tarjeta que el resto de secciones

type TabKey = "users" | "roles" | "permissions";

const TABS: { key: TabKey; label: string; addLabel: string }[] = [
  { key: "users", label: "Usuarios", addLabel: "Añadir usuario" },
  { key: "roles", label: "Roles", addLabel: "Añadir rol" },
  { key: "permissions", label: "Permisos", addLabel: "Añadir permiso" },
];

export default function Users() {
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.userReducer.user);
  const [tab, setTab] = useState<TabKey>("users");
  const [createOpen, setCreateOpen] = useState(false);
  const [me, setMe] = useState<any>(null);

  // Refresca el usuario actual y sus permisos (GET /employee/me) sin volver a
  // hacer login. Si ya no tiene gestionar_usuarios, la ruta deja de ser accesible.
  const refreshMe = useCallback(() => {
    getCurrentEmployee()
      .then((response) => {
        const { permissions, ...employee } = response.data ?? {};
        const stored = JSON.parse(localStorage.getItem("aedas_user") ?? "{}");
        const updated = {
          ...stored,
          employee: { ...stored.employee, ...employee },
          permissions: permissions ?? stored.permissions,
        };
        localStorage.setItem("aedas_user", JSON.stringify(updated));
        dispatch(userUpdate(updated));
        setMe(response.data);
      })
      .catch((error) => handleApiError(error));
  }, [dispatch]);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const current = TABS.find((item) => item.key === tab)!;

  const tabProps: UsersTabProps = {
    selfId: me?.id ?? user?.employee?.id,
    selfRolId: me?.id_rol ?? me?.rol?.id ?? user?.employee?.id_rol,
    createOpen,
    onCreateClose: () => setCreateOpen(false),
    onSelfChanged: refreshMe,
  };

  return (
    <div className="entityPage">
      <div className="main-container entityPage__card p-4">
        <SectionHeader title="Gestión de usuarios" addLabel={current.addLabel} onAdd={() => setCreateOpen(true)} />

        <Tabs
          className="usersPage__tabs"
          value={tab}
          onChange={(_e, value: TabKey) => {
            setTab(value);
            setCreateOpen(false);
          }}
        >
          {TABS.map((item) => (
            <Tab key={item.key} id={`users-tab-${item.key}`} value={item.key} label={item.label} />
          ))}
        </Tabs>

        <div className="entityPage__table usersPage__panel">
          {tab === "users" ? <UsersTab {...tabProps} /> : null}
          {tab === "roles" ? <RolesTab {...tabProps} /> : null}
          {tab === "permissions" ? <PermissionsTab {...tabProps} /> : null}
        </div>
      </div>
    </div>
  );
}
